import React from "react";
import ReactDOM from "react-dom";
import { LDFlagSet } from "launchdarkly-react-client-sdk";
import {
  isSmallDeviceWidth,
  NotificationToast,
  SlideDown,
} from "component-library";
import { withTranslation, WithTranslation, TFunction } from "i18n";
import { BasicInfo, FacetCategory } from "models";

import { Action } from "components/common/Action/Action";

import { BasicInfoDetails, IBasicInfoDetailsProps } from "../BasicInfoDetails";
import { EditPanel } from "../EditPanel";

import {
  BASIC_INFO_DETAILS_ARRAYS,
  BASIC_INFO_VIEW_TO_DETAILS_MAPPING,
  getHintResolver,
  IBasicInfoSectionContent,
  isVisibleField,
} from "./BasicInfoSectionContent";
import { BasicInfoConverter, FacetConverter } from "./converters";
import { IBasicInfoViewModel } from "./viewModels";

import compose from "shared/utils/compose";
import {
  LaunchDarklyClientWithFlags,
  withLaunchDarklyConsumerHOC,
} from "shared-services";
import { heapTrackingForBasicInfoSection } from "app/helpers/heapTracking";
import "./BasicInfoSection.scss";

export const CONSTANTLY_VISIBLE_MOBILE_ITEMS_COUNT = 6;
const TOAST_LIFE_TIME = 3000;
const RESERVED_SPACE = 20;

export interface IBasicInfoDetailData {
  name: string;
  values: string[];
  hint?: string;
}

export interface IBasicInfoSectionState {
  basicInfoDetails: IBasicInfoDetailsProps[];
  isToastVisible: boolean;
}

interface IBasicInfoSectionComposeProps {
  basicInfo?: BasicInfo;
  basicInfoFacets: FacetCategory[];
  editBasicInfo: () => void;
  content: IBasicInfoSectionContent;
  expandableContainer: React.RefObject<HTMLDivElement>;
  outerContainer: React.RefObject<HTMLDivElement>;
  isExpanded: boolean;
  isReadonly: boolean;
  toggleExpand: () => void;
  isToastVisible: boolean;
  closeToast: () => void;
}

export interface IBasicInfoSectionProps
  extends IBasicInfoSectionComposeProps,
    LaunchDarklyClientWithFlags,
    WithTranslation {}

export class BasicInfoSection extends React.Component<
  IBasicInfoSectionProps,
  IBasicInfoSectionState
> {
  private rootRef = React.createRef<HTMLDivElement>();
  private editPanelRef = React.createRef<HTMLDivElement>();
  private expandToggleRef = React.createRef<HTMLAnchorElement>();
  private topItemsRefs: React.RefObject<HTMLDivElement>[] = [];
  private additionalItemsRefs: React.RefObject<HTMLDivElement>[] = [];

  constructor(props: IBasicInfoSectionProps) {
    super(props);

    this.state = {
      isToastVisible: this.props.isToastVisible,
      basicInfoDetails:
        BasicInfoSection.getDerivedStateFromProps(this.props)
          .basicInfoDetails ?? [],
    };
  }

  public render() {
    const {
      editBasicInfo,
      expandableContainer,
      content,
      isExpanded,
      isReadonly,
    } = this.props;
    const { basicInfoDetails } = this.state;

    return (
      <section
        className="c-basic-info-section"
        ref={this.rootRef}
        aria-label={content.detailsAlternativeText}
      >
        <div
          ref={this.editPanelRef}
          className="c-basic-info-section__edit-panel"
        >
          <EditPanel
            header={content.detailsSectionName}
            editFn={() => {
              heapTrackingForBasicInfoSection();
              editBasicInfo();
            }}
            hideEdit={isReadonly}
            editLinkAltText={content.editLinkAltText}
          />
        </div>
        <dl
          className="c-basic-info-section__details-definition-list"
          aria-label={content.detailsListAlternativeText}
        >
          {this.renderConstantItems(basicInfoDetails)}

          {expandableContainer.current &&
            this.renderHiddenItems(
              basicInfoDetails,
              expandableContainer.current,
              isExpanded
            )}
        </dl>
        {this.renderExpandToggle(content, isExpanded)}
        {this.renderToast()}
      </section>
    );
  }

  public componentDidMount() {
    window.addEventListener("resize", this.checkVisibility, false);
  }

  public componentDidUpdate() {
    this.checkVisibility();
  }

  private renderToast = () => {
    const { content } = this.props;
    const { isToastVisible } = this.props;

    const link = (
      <span className="c-shortlist-section__toast-text">
        {content.performerContacted + " "}
      </span>
    );

    return (
      <NotificationToast
        show={isToastVisible}
        handleHide={this.hideToast}
        text={link}
        timeoutDuration={TOAST_LIFE_TIME}
      />
    );
  };

  private hideToast = () => this.props.closeToast();

  public static getDerivedStateFromProps(
    nextProps: IBasicInfoSectionProps & LaunchDarklyClientWithFlags
  ) {
    let stateUpdates: Partial<IBasicInfoSectionState> = {};

    if (nextProps?.basicInfo && nextProps?.basicInfoFacets?.length > 0) {
      const { basicInfoFacets, basicInfo, content, isReadonly, flags, t } =
        nextProps;

      stateUpdates = {
        // can't be fixed because use method of component
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        basicInfoDetails: GET_BASIC_INFO_DETAILS_LIST(
          basicInfoFacets,
          basicInfo,
          content,
          isReadonly,
          t,
          flags
        ),
      };
    }

    return stateUpdates;
  }

  private renderConstantItems = (items: IBasicInfoDetailsProps[]) => {
    this.topItemsRefs = [];

    return items.map((detail) => {
      const detailRef = React.createRef<HTMLDivElement>();
      this.topItemsRefs.push(detailRef);

      return (
        <div
          key={detail.name}
          ref={detailRef}
          className="c-basic-info-section__top-row"
        >
          <BasicInfoDetails {...detail} />
        </div>
      );
    });
  };

  private renderHiddenItems = (
    items: IBasicInfoDetailsProps[],
    container: HTMLDivElement,
    isExpanded: boolean
  ) => {
    this.additionalItemsRefs = [];

    return ReactDOM.createPortal(
      <SlideDown
        className="c-basic-info-section__expandable-area"
        closed={!isExpanded}
        transitionOnAppear={false}
      >
        <dl className="c-basic-info-section__details-list">
          {items.map((detail) => {
            const detailRef = React.createRef<HTMLDivElement>();
            this.additionalItemsRefs.push(detailRef);

            return (
              <div
                key={detail.name}
                ref={detailRef}
                className="c-basic-info-section__details"
              >
                <BasicInfoDetails {...detail} />
              </div>
            );
          })}
        </dl>
      </SlideDown>,
      container
    );
  };

  private renderExpandToggle = (
    content: IBasicInfoSectionContent,
    isExpanded: boolean
  ) => (
    <Action
      forwardRef={this.expandToggleRef}
      className="c-basic-info-section__expand-toggle g-alternative-link g-alternative-link--md"
      onClick={this.props.toggleExpand}
    >
      {isExpanded
        ? content.detailsSectionShowLess
        : content.detailsSectionShowMore}
    </Action>
  );

  private checkVisibility = () => {
    const {
      outerContainer: { current: outerContainer },
    } = this.props;
    const { current: root } = this.rootRef;
    const { current: editPanel } = this.editPanelRef;

    if (root && outerContainer && editPanel) {
      const { clientHeight: maxHeight = 0 } = outerContainer;
      const { clientHeight: editPanelHeight = 0 } = editPanel;

      const hideTopRow = (
        topRow: Element | null,
        hiddenRow: Element | null
      ) => {
        if (topRow) {
          topRow.classList.add("hidden");
          topRow.setAttribute("aria-hidden", "true");
        }

        if (hiddenRow) {
          hiddenRow.classList.remove("hidden");
          hiddenRow.setAttribute("aria-hidden", "false");
        }
      };

      const showTopRow = (
        topRow: Element | null,
        hiddenRow: Element | null
      ) => {
        topRow?.classList?.remove("hidden");
        hiddenRow?.classList?.add("hidden");
      };

      let hasHiddenElements = false;

      if (!isSmallDeviceWidth()) {
        let heightLeft = maxHeight - editPanelHeight - RESERVED_SPACE;

        this.topItemsRefs.forEach(({ current: topRow }, index) => {
          if (topRow) {
            heightLeft -= topRow.scrollHeight;

            const { current: hiddenRow } = this.additionalItemsRefs[index];

            if (heightLeft < 0) {
              hideTopRow(topRow, hiddenRow);
              hasHiddenElements = true;
            } else {
              showTopRow(topRow, hiddenRow);
            }
          }
        });
      } else {
        this.topItemsRefs.forEach(({ current: topRow }, index) => {
          const { current: hiddenRow } = this.additionalItemsRefs[index];

          if (index < CONSTANTLY_VISIBLE_MOBILE_ITEMS_COUNT) {
            showTopRow(topRow, hiddenRow);
          } else {
            hideTopRow(topRow, hiddenRow);
            hasHiddenElements = true;
          }
        });
      }

      const { current: expandToggle } = this.expandToggleRef;

      if (expandToggle) {
        if (!hasHiddenElements) {
          expandToggle.classList.add("hidden");
        } else {
          expandToggle.classList.remove("hidden");
        }
      }
    }
  };

  public static getBasicInfoDetails = (
    content: IBasicInfoSectionContent,
    basicInfoView: IBasicInfoViewModel,
    getHint: (fieldName: keyof IBasicInfoViewModel) => string | undefined,
    t: TFunction,
    flags?: LDFlagSet
  ) => {
    const result = new Array<IBasicInfoDetailData>();

    if (basicInfoView) {
      BASIC_INFO_VIEW_TO_DETAILS_MAPPING.forEach((map) => {
        const detailsValue = basicInfoView[map.fieldName];
        const isEmptyArray =
          Array.isArray(detailsValue) && detailsValue.length === 0;

        if (
          detailsValue &&
          !isEmptyArray &&
          isVisibleField(map.fieldName, flags)
        ) {
          if (!content[map.contentName]) {
            content[map.contentName] = t(
              `performer:profile.basicInfo.${map.fieldName}`
            );
          }

          const detailsItem: IBasicInfoDetailData = {
            name: content[map.contentName],
            values: [],
            hint: getHint(map.fieldName),
          };

          if (Array.isArray(detailsValue)) {
            if (BASIC_INFO_DETAILS_ARRAYS.includes(map.fieldName)) {
              detailsItem.values.push(...detailsValue);
            } else {
              detailsItem.values.push(detailsValue.join(", "));
            }
          } else {
            detailsItem.values.push(detailsValue);
          }

          result.push(detailsItem);
        }
      });
    }

    return result;
  };
}

export const GET_BASIC_INFO_DETAILS_LIST = (
  basicInfoFacets: FacetCategory[],
  basicInfo: BasicInfo,
  content: IBasicInfoSectionContent,
  isViewMode: boolean,
  t: TFunction,
  flags?: LDFlagSet
) => {
  const facetsView = FacetConverter.convertToView(basicInfoFacets);
  const basicInfoView = BasicInfoConverter.convertToView(
    basicInfo,
    facetsView,
    content,
    t,
    isViewMode
  );
  const getHint = getHintResolver(basicInfo, content, isViewMode);

  return BasicInfoSection.getBasicInfoDetails(
    content,
    basicInfoView,
    getHint,
    t,
    flags
  );
};

export default compose(
  withTranslation(),
  withLaunchDarklyConsumerHOC()
)(BasicInfoSection) as React.ComponentType<IBasicInfoSectionComposeProps>;
